import { useEffect } from "react";
import { isInsideCellControls } from "./focusLevel";
import { type GridController, isFocusLost } from "./gridController";

const OPEN_CONTROL_SELECTOR = "[data-dt-controls] [aria-expanded='true']";

const isOnControls = (root: HTMLElement, target: EventTarget | null) =>
    target instanceof Node &&
    root.contains(target) &&
    isInsideCellControls(target);

const isInsideCell = (root: HTMLElement, target: EventTarget | null) =>
    target instanceof Element &&
    root.contains(target) &&
    target.closest("[role='gridcell']") !== null;

/**
 * Escape inside a popup opened from a cell control (a Picker, the row-actions
 * menu) closes it and then returns focus to the row or cell instead of the
 * control, so the next Escape runs the grid's Escape ladder. Nested popup
 * levels close one Escape at a time; focus moves once the last one closes,
 * which can be after the popup's exit animation. In-cell controls that handle
 * Escape themselves (an inline input that blurs or unmounts) get the same
 * return when their Escape leaves focus on the page body.
 */
export const useControlPopupEscape = ({
    controller,
    returnFromControls,
}: {
    controller: GridController;
    returnFromControls: () => void;
}) => {
    useEffect(() => {
        let pendingTrigger: HTMLElement | null = null;
        let frame: number | null = null;

        const returnIfClosed = (target: EventTarget | null) => {
            const { root } = controller;
            if (
                pendingTrigger === null ||
                root === null ||
                pendingTrigger.getAttribute("aria-expanded") === "true" ||
                !isOnControls(root, target)
            ) {
                return;
            }
            pendingTrigger = null;
            returnFromControls();
        };
        const returnIfDropped = () => {
            if (controller.isFocusWithin.get() && isFocusLost()) {
                returnFromControls();
            }
        };
        const clearPending = () => {
            pendingTrigger = null;
        };
        const schedule = (check: () => void) => {
            if (frame !== null) {
                cancelAnimationFrame(frame);
            }
            frame = requestAnimationFrame(() => {
                frame = null;
                check();
            });
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            const { root } = controller;
            if (event.key !== "Escape" || root === null) {
                pendingTrigger = null;
                return;
            }
            pendingTrigger = root.querySelector<HTMLElement>(
                OPEN_CONTROL_SELECTOR,
            );
            if (pendingTrigger !== null) {
                schedule(() => returnIfClosed(document.activeElement));
            } else if (isInsideCell(root, event.target)) {
                schedule(returnIfDropped);
            }
        };
        const handleFocusIn = (event: FocusEvent) => {
            returnIfClosed(event.target);
        };

        document.addEventListener("keydown", handleKeyDown, true);
        document.addEventListener("focusin", handleFocusIn, true);
        document.addEventListener("pointerdown", clearPending, true);
        return () => {
            document.removeEventListener("keydown", handleKeyDown, true);
            document.removeEventListener("focusin", handleFocusIn, true);
            document.removeEventListener("pointerdown", clearPending, true);
            if (frame !== null) {
                cancelAnimationFrame(frame);
            }
        };
    }, [controller, returnFromControls]);
};
